"use client";

import { useEffect, useRef, useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { submitFeedback } from "@/lib/services/feedbackService";
import { HttpError } from "@/lib/http";

const MIN_LENGTH = 10;
const MAX_LENGTH = 2000;

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const trimmed = feedback.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_LENGTH;
  const tooLong = trimmed.length > MAX_LENGTH;
  const canSubmit = trimmed.length >= MIN_LENGTH && !tooLong && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await submitFeedback(trimmed);
      toast.success("Спасибо за ваш отзыв!");
      setOpen(false);
      setFeedback("");
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.status === 429) {
          toast.error("Вы достигли лимита отзывов на сегодня. Попробуйте завтра.");
        } else if (error.status === 422) {
          toast.error(error.message);
        } else {
          toast.error("Не удалось отправить отзыв. Попробуйте позже.");
        }
      } else {
        toast.error("Не удалось отправить отзыв. Попробуйте позже.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md border border-border px-3 py-1.5 hover:bg-accent/50"
      >
        <MessageSquarePlus className="size-3.5" />
        Обратная связь
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute bottom-full left-0 mb-2 w-72 rounded-xl border border-border bg-popover p-4 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2"
        >
          <p className="text-sm font-medium text-foreground mb-3">
            Обратная связь
          </p>
          <Textarea
            placeholder="Что вы думаете о платформе?"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            maxLength={MAX_LENGTH}
            autoFocus
            disabled={submitting}
            className="resize-none text-sm"
          />
          {tooShort && (
            <p className="text-[11px] text-destructive mt-1">
              Минимум {MIN_LENGTH} символов
            </p>
          )}
          <Button
            size="sm"
            className="w-full mt-3 bg-foreground text-background hover:bg-foreground/80"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                <Loader2 className="size-3.5 animate-spin mr-1.5" />
                Отправка...
              </>
            ) : (
              "Отправить"
            )}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            Опрос сделан платформой
          </p>
        </div>
      )}
    </div>
  );
}
